import { useState } from "react";
import { Alert, Box, Button, Drawer, FormControlLabel, IconButton, Radio, RadioGroup, Stack, TextField, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { IElegibilityPlans, PriceBand, PriceVersion } from "../../Setup.types";
import { RN63_BANDS, VersionInput, addPriceVersion, correctPriceVersion, currentVersionOn, legacyPrefillCents, setInactiveMode } from "./planPanel.api";

type Side = "active" | "inactive";
type Mode = "SAME_AS_ACTIVE" | "SINGLE" | "AGE_BANDS";
type BandRow = { fromAge: string; toAge: string; value: string };
/** Formulário aberto: versão nova (reajuste ou a primeira) ou correção da versão com aquele `validFrom`. */
type Editing = { action: "add" } | { action: "correct"; validFrom: string };

interface IProps {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
  plan: IElegibilityPlans;
  side: Side;
  invoicingType?: string;
  onSaved: (plan: IElegibilityPlans) => void;
}

/** Dia de hoje em Brasília (YYYY-MM-DD), o mesmo "hoje" do backend para escolher a versão vigente. */
export const saoPauloToday = () => new Intl.DateTimeFormat("en-CA", { timeZone: "America/Sao_Paulo" }).format(new Date());

/** "2026-11-01" -> "01/11/2026". */
export const ptDay = (day: string) => day.split("-").reverse().join("/");

/** Carimbo ISO do servidor -> dd/mm/aaaa em Brasília. */
const ptDateOf = (iso: string) => new Date(iso).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });

/** 123456 -> "1.234,56". Mesmo formato do espelho `planValue` do backend. */
export function centsToReais(cents: number): string {
  const reais = Math.floor(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${reais},${String(cents % 100).padStart(2, "0")}`;
}

const MONEY = /^\d{1,3}(\.\d{3})*(,\d{1,2})?$|^\d+(,\d{1,2})?$/;

/** "1.234,56" -> 123456. Vazio -> null. Texto que não é dinheiro -> NaN, que nunca vai ao servidor. */
export function reaisToCents(text: string): number | null {
  const t = text.trim().replace(/^R\$\s?/, "");
  if (!t) return null;
  if (!MONEY.test(t)) return NaN;
  const [reais, centavos = ""] = t.replace(/\./g, "").split(",");
  return Number(reais) * 100 + Number(centavos.padEnd(2, "0"));
}

/** "Valor único · R$ 980,00" ou "Por faixa, 10 faixas". */
export function versionSummary(v: PriceVersion): string {
  return v.kind === "SINGLE" ? `Valor único · R$ ${centsToReais(v.singleCents as number)}` : `Por faixa, ${v.bands?.length ?? 0} faixas`;
}

/** Mensagem do 400/404 do backend (corpo `{ data: { message } }`); sem ela, o texto genérico. */
export function serverMessage(error: unknown, fallback: string): string {
  return (error as { response?: { data?: { data?: { message?: string } } } })?.response?.data?.data?.message ?? fallback;
}

const bandText = (b: PriceBand) =>
  `${b.fromAge}${b.toAge === null ? " ou mais" : ` a ${b.toAge}`}: ${b.cents === null ? "a confirmar" : `R$ ${centsToReais(b.cents)}`}`;

const rowsOf = (bands: PriceBand[]): BandRow[] =>
  bands.map((b) => ({ fromAge: String(b.fromAge), toAge: b.toAge === null ? "" : String(b.toAge), value: b.cents === null ? "" : centsToReais(b.cents) }));

const versionsOf = (plan: IElegibilityPlans, side: Side): PriceVersion[] =>
  side === "active" ? plan.activePrice ?? [] : plan.inactivePrice?.mode === "OWN" ? plan.inactivePrice.versions : [];

/**
 * Spec tabela de preço por faixa §4.2: painel lateral do preço de um plano (ativos ou inativos).
 * invariant: só grava pelas rotas do painel; cada gravação devolve o plano gravado por `onSaved`, para o
 * Salvar geral do setup não mandar dado velho.
 */
export function PlanPricePanel({ open, onClose, workspaceId, plan, side, invoicingType, onSaved }: IProps) {
  const today = saoPauloToday();
  const planCode = plan.planCode as string;
  const versions = versionsOf(plan, side);
  const current = currentVersionOn(versions, today);
  const future = versions.filter((v) => v.validFrom > today);
  const own = plan.inactivePrice?.mode === "OWN" ? plan.inactivePrice : null;
  const hasActive = (plan.activePrice?.length ?? 0) > 0;

  const [mode, setMode] = useState<Mode>(() => {
    if (side === "inactive" && plan.inactivePrice?.mode === "SAME_AS_ACTIVE") return "SAME_AS_ACTIVE";
    return (current ?? versions[0])?.kind ?? (invoicingType === "FAIXA_ETARIA" ? "AGE_BANDS" : "SINGLE");
  });
  const [editing, setEditing] = useState<Editing | null>(null);
  const [validFrom, setValidFrom] = useState("");
  // Spec 4.5: só o legado inequívoco pré-preenche, e só ativos sem versão gravada.
  const [single, setSingle] = useState(() => {
    const legacy = side === "active" && versions.length === 0 ? legacyPrefillCents(plan.planValue) : null;
    return legacy === null ? "" : centsToReais(legacy);
  });
  const [bands, setBands] = useState<BandRow[]>(() => rowsOf(RN63_BANDS));
  const [inactiveName, setInactiveName] = useState(own?.planName ?? "");
  const [inactiveCode, setInactiveCode] = useState(own?.planCode ?? "");
  const [confirmSame, setConfirmSame] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Sem versão, o formulário da primeira já fica aberto.
  const form: Editing | null = mode === "SAME_AS_ACTIVE" ? null : editing ?? (versions.length === 0 ? { action: "add" } : null);
  // Sem formulário aberto, o rádio diz o tipo da versão vigente, não o do último formulário.
  const shownMode: Mode = form || mode === "SAME_AS_ACTIVE" ? mode : (current ?? versions[0])?.kind ?? mode;

  function fill(v: PriceVersion) {
    setSingle(v.singleCents === undefined ? "" : centsToReais(v.singleCents));
    setBands(rowsOf(v.bands ?? RN63_BANDS));
  }

  /** Sem tipo escolhido, o reajuste começa no tipo vigente. */
  function startAdd(kind?: Mode) {
    const latest = versions[versions.length - 1];
    if (latest) fill(latest);
    setMode(kind ?? (current ?? latest)?.kind ?? mode);
    setValidFrom("");
    setError(null);
    setEditing({ action: "add" });
  }

  function startCorrect(v: PriceVersion) {
    setMode(v.kind);
    fill(v);
    setError(null);
    setEditing({ action: "correct", validFrom: v.validFrom });
  }

  async function applySameAsActive() {
    setConfirmSame(false);
    setSaving(true);
    setError(null);
    try {
      onSaved(await setInactiveMode(workspaceId, planCode, { mode: "SAME_AS_ACTIVE" }));
      setMode("SAME_AS_ACTIVE");
      setEditing(null);
    } catch (e) {
      setError(serverMessage(e, "Não foi possível gravar o modo de inativos."));
    } finally {
      setSaving(false);
    }
  }

  function handleMode(next: Mode) {
    setError(null);
    if (next !== "SAME_AS_ACTIVE") {
      // Trocar o tipo com versões gravadas é um reajuste desse tipo.
      return form ? setMode(next) : startAdd(next);
    }
    // Decisão do Ruben: voltar para Igual ao de ativos apaga as versões próprias de inativos.
    if (own && own.versions.length > 0) return setConfirmSame(true);
    applySameAsActive();
  }

  function buildInput(): Omit<VersionInput, "validFrom"> | string {
    if (mode === "SINGLE") {
      const cents = reaisToCents(single);
      if (cents === null) return "Informe o valor.";
      if (Number.isNaN(cents)) return "Valor inválido: use o formato 1.234,56.";
      return { kind: "SINGLE", singleCents: cents };
    }
    const out: PriceBand[] = [];
    for (const [i, b] of bands.entries()) {
      const cents = reaisToCents(b.value);
      if (Number.isNaN(cents)) return `Valor inválido na faixa ${i + 1}: use o formato 1.234,56.`;
      // Idade vazia ou não numérica vai como null e o servidor diz qual faixa está errada.
      const age = (text: string) => (text.trim() === "" ? NaN : Number(text));
      out.push({ fromAge: age(b.fromAge), toAge: b.toAge.trim() === "" ? null : age(b.toAge), cents });
    }
    return { kind: "AGE_BANDS", bands: out };
  }

  async function handleSave() {
    if (!form) return;
    const input = buildInput();
    if (typeof input === "string") return setError(input);
    if (form.action === "add" && !validFrom) return setError("Informe a data de vigência.");
    setSaving(true);
    setError(null);
    try {
      const nameChanged = inactiveName !== (own?.planName ?? "") || inactiveCode !== (own?.planCode ?? "");
      if (side === "inactive" && (!own || nameChanged)) {
        onSaved(
          await setInactiveMode(workspaceId, planCode, {
            mode: "OWN",
            planName: inactiveName.trim() || undefined,
            planCode: inactiveCode.trim() || undefined,
          }),
        );
      }
      const saved =
        form.action === "add"
          ? await addPriceVersion(workspaceId, planCode, side, { validFrom, ...input })
          : await correctPriceVersion(workspaceId, planCode, side, form.validFrom, input);
      onSaved(saved);
      setEditing(null);
    } catch (e) {
      setError(serverMessage(e, "Não foi possível gravar o preço."));
    } finally {
      setSaving(false);
    }
  }

  const setBand = (i: number, patch: Partial<BandRow>) => setBands((prev) => prev.map((b, j) => (j === i ? { ...b, ...patch } : b)));

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", md: 560 }, p: 3 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">
          {side === "active" ? "Preço de ativos" : "Preço de inativos"} · {plan.planName}
        </Typography>
        <IconButton onClick={onClose} aria-label="fechar">
          <CloseIcon />
        </IconButton>
      </Stack>

      <Typography sx={{ fontWeight: 600 }}>Valor integral mensal por pessoa</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        o valor cobrado pela operadora, sem descontar a parte da empresa
      </Typography>

      <RadioGroup value={shownMode} onChange={(e) => handleMode(e.target.value as Mode)} sx={{ mb: 2 }}>
        {side === "inactive" && (
          <>
            <FormControlLabel value="SAME_AS_ACTIVE" control={<Radio />} label="Igual ao de ativos" disabled={!hasActive || saving} />
            {!hasActive && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 4, mt: -1 }}>
                Cadastre o preço de ativos no painel antes
              </Typography>
            )}
          </>
        )}
        <FormControlLabel value="SINGLE" control={<Radio />} label="Valor único" disabled={saving} />
        <FormControlLabel value="AGE_BANDS" control={<Radio />} label="Por faixa etária" disabled={saving} />
      </RadioGroup>

      {confirmSame && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <>
              <Button onClick={() => setConfirmSame(false)}>Cancelar</Button>
              <Button onClick={applySameAsActive}>Confirmar</Button>
            </>
          }
        >
          As versões próprias de inativos deste plano serão apagadas.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {mode === "SAME_AS_ACTIVE" && plan.inactivePrice?.mode === "SAME_AS_ACTIVE" && (
        <Typography sx={{ mb: 2 }}>Os inativos pagam a mesma tabela de ativos.</Typography>
      )}

      {mode !== "SAME_AS_ACTIVE" &&
        [current, ...future].map((v, i) =>
          v ? (
            <Box key={v.validFrom} sx={{ border: 1, borderColor: "divider", borderRadius: 2, p: 2, mb: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontWeight: 600 }}>
                  {v === current ? `Vigente desde ${ptDay(v.validFrom)}` : `${i === 1 ? "Próximo reajuste em" : "Reajuste em"} ${ptDay(v.validFrom)}`}
                </Typography>
                <Button size="small" onClick={() => startCorrect(v)} disabled={saving}>
                  Corrigir
                </Button>
              </Stack>
              <Typography variant="body2" color="text.secondary">{`registrada${v.recordedByName ? ` por ${v.recordedByName}` : ""} em ${ptDateOf(v.recordedAt)}`}</Typography>
              {v.correctedAt && (
                <Typography variant="body2" color="text.secondary">{`corrigida${v.correctedByName ? ` por ${v.correctedByName}` : ""} em ${ptDateOf(v.correctedAt)}`}</Typography>
              )}
              <Typography variant="body2" sx={{ mt: 1 }}>{versionSummary(v)}</Typography>
              {v.kind === "AGE_BANDS" &&
                v.bands?.map((b) => (
                  <Typography key={b.fromAge} variant="body2" color="text.secondary">
                    {bandText(b)}
                  </Typography>
                ))}
            </Box>
          ) : null,
        )}

      {mode !== "SAME_AS_ACTIVE" && versions.length > 0 && !editing && (
        <Button variant="outlined" onClick={() => startAdd()} disabled={saving} sx={{ alignSelf: "flex-start" }}>
          Novo preço (reajuste)
        </Button>
      )}

      {form && (
        <Stack sx={{ mt: 1, gap: 2.5 }}>
          <Typography sx={{ fontWeight: 600 }}>
            {form.action === "correct" ? `Corrigir a versão de ${ptDay(form.validFrom)}` : versions.length > 0 ? "Novo preço (reajuste)" : "Primeiro preço"}
          </Typography>
          {form.action === "add" && (
            <TextField type="date" label="Vigente desde" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          )}
          {side === "inactive" && (
            <>
              <TextField label="Nome do plano de inativos (opcional)" value={inactiveName} onChange={(e) => setInactiveName(e.target.value)} />
              <TextField
                label="Código do plano de inativos (opcional)"
                value={inactiveCode}
                onChange={(e) => setInactiveCode(e.target.value)}
                helperText="Em branco, vale o nome e o código do plano de ativos."
              />
            </>
          )}
          {mode === "SINGLE" ? (
            <TextField label="Valor mensal (R$)" value={single} placeholder="1.234,56" onChange={(e) => setSingle(e.target.value)} inputProps={{ inputMode: "decimal" }} />
          ) : (
            <Box component="table" sx={{ borderCollapse: "collapse", "& th, & td": { p: 0.5, textAlign: "left" } }}>
              <thead>
                <tr>
                  <th>De</th>
                  <th>Até</th>
                  <th>R$</th>
                </tr>
              </thead>
              <tbody>
                {bands.map((b, i) => (
                  <tr key={i}>
                    <td>
                      <TextField size="small" sx={{ width: 80 }} value={b.fromAge} onChange={(e) => setBand(i, { fromAge: e.target.value })} inputProps={{ "aria-label": `De, faixa ${i + 1}`, inputMode: "numeric" }} />
                    </td>
                    <td>
                      <TextField
                        size="small"
                        sx={{ width: 100 }}
                        value={b.toAge}
                        placeholder={i === bands.length - 1 ? "ou mais" : ""}
                        onChange={(e) => setBand(i, { toAge: e.target.value })}
                        inputProps={{ "aria-label": `Até, faixa ${i + 1}`, inputMode: "numeric" }}
                      />
                    </td>
                    <td>
                      <TextField size="small" value={b.value} placeholder="a confirmar" onChange={(e) => setBand(i, { value: e.target.value })} inputProps={{ "aria-label": `R$, faixa ${i + 1}`, inputMode: "decimal" }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Box>
          )}
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              Salvar
            </Button>
            {editing && (
              <Button onClick={() => setEditing(null)} disabled={saving}>
                Cancelar
              </Button>
            )}
          </Stack>
        </Stack>
      )}
    </Drawer>
  );
}
